import React, { useState, useContext } from 'react';
import axios from 'axios';
import PasswordValidator from 'password-validator';
import PhoneInput from 'react-phone-number-input';
import { ReactComponent as VisibleIcon } from 'Assets/eye.svg';
import { ReactComponent as HiddenIcon } from 'Assets/eye.slash.svg';
import 'react-phone-number-input/style.css';
import { AppContext, AuthContext } from 'Context';
import PasswordRequirements from 'Pages/public/authentication/newSign-in/PasswordRequirements';
import './createUser.css';
import cn from 'classnames';
import { baseUrl } from 'Api';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import ProjectorAcknowlegingTxt from 'Pages/public/authentication/ProjectorAcknowlegingTxt';
import authStyles from '../Pages/public/authentication/auth.module.css';
import Logo from '../Assets/logoProjector.png';
import styles from '../Pages/public/authentication/components/input-field/index.module.css';

function CreateUser() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState(false);
  const [loader, setloader] = useState(false);
  const { setapiCALL, handleClick, getSubscription, autoSignIn } = useContext(AppContext);
  const [emailExists, setEmailExists] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [err, seterr] = useState('');
  const userType = location.state?.userType || 'quick'; // Default to 'quick' if not provided
  const [numberErr, setNumberErr] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const myToken = getToken();
  const passwordSchema = new PasswordValidator()
    .is()
    .min(12)
    .has()
    .symbols(1)
    .has()
    .digits(1)
    .has()
    .uppercase(1)
    .lowercase(1);
  const hasUpperCase = /[A-Z]/.test(password);
  const passwordHealth = password
    ? password.length < 12 || !hasUpperCase
      ? 'weak'
      : passwordSchema.validate(password)
        ? password.length >= 12
          ? 'strong'
          : 'medium'
        : 'weak'
    : '';
  const handleEmailChange = ({ target: { value } }) => {
    setEmail(value);
    if (value.includes('@')) {
      setIsLoading(true); // Activate spinner only when '@' is typed
      axios
        .post(`${baseUrl}/signupEmail`, {
          email: value,
        })
        .then((response) => {
          setEmailExists(response.data);
          setIsLoading(false); // Deactivate spinner when response is received
        })
        .catch((error) => {
          console.error(error);
          setIsLoading(false); // Deactivate spinner in case of error
        });
    }
  };

  const validateText = (text) => {
    const textRegex = /^[a-zA-Z\s]*$/;
    return textRegex.test(text);
  };

  const handleFirstNameChange = (event) => {
    const { value } = event.target;
    if (validateText(value)) {
      setFirstName(value);
    }
  };

  const handleLastNameChange = (event) => {
    const { value } = event.target;
    if (validateText(value)) {
      setLastName(value);
    }
  };

  const handlePasswordChange = (event) => {
    const newPassword = event.target.value;
    if (newPassword.length <= 30) {
      setPassword(newPassword);
    }
  };

  const handlePhoneNumberChange = (value) => {
    setPhoneNumber(value || '');
    // Clear error when user starts typing
    if (numberErr) {
      setNumberErr('');
    }
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const validateEmail = (email) => emailRegex.test(email);

  // Switch to the created user's account
  const switchToUserAccount = async (userDetails) => {
    try {
      const formData = new FormData();
      formData.append('token', myToken);
      formData.append('user_id', userDetails.userId || userDetails.id);
      formData.append('user_token', userDetails.token);

      const response = await axios.post(`${baseUrl}/managerUserLogs`, formData);

      if (response?.data?.success === true) {
        handleClick(userDetails);
        localStorage.setItem('firstname', userDetails?.firstname || '');
        localStorage.setItem('lastname', userDetails?.lastname || '');
        sessionStorage.setItem('quickUserEmail', email);
        navigate('/quick-user-upload', { replace: true });
        getSubscription();
        autoSignIn();
      } else {
        // If switching fails, navigate to plans page as fallback
        navigate('/plans');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error switching to user account:', error);
      // If switching fails, navigate to plans page as fallback
      navigate('/plans');
    }
  };

  const validatePhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return false;
    // Extract only digits from the phone number
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    // For US numbers, check if we have exactly 11 digits (1 + 10 digits) or 10 digits
    return digitsOnly.length === 11 || digitsOnly.length === 12;
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  const updateProfile = async (phoneNumber, emails, myToken) => {
    const apiUrl = `${baseUrl}/updateMyProfile`;
    const requestBody = {
      mobile: phoneNumber,
      email: emails,
      token: myToken,
    };
    try {
      const response = await axios.post(apiUrl, requestBody);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('API error:', error);
      if (
        error.response
        && error.response.data
        && error.response.data.message
      ) {
        setNumberErr(error.response.data.message);
      } else {
        setNumberErr('An error occurred while updating the profile.');
      }
      throw error;
    }
  };

  const handleSubmit = async () => {
    try {
      // Email validation is required for both quick and withPlan users
      if (!validateEmail(email)) {
        seterr('Please enter a valid email address.');
        setloader(false);
        return;
      }

      // Additional validation for 'withPlan' users
      if (userType === 'withPlan') {
        // Update profile first
        const updateProfileResponse = await updateProfile(phoneNumber, email, myToken);
        if (!updateProfileResponse.success) {
          // eslint-disable-next-line no-console
          console.error('Profile update failed:', updateProfileResponse.message);
          setNumberErr(updateProfileResponse);
          setloader(false);
          return;
        }

        // Validate all required fields
        if (!firstName || !lastName) {
          seterr('First name and last name are required.');
          setloader(false);
          return;
        }

        if (!phoneNumber || !validatePhoneNumber(phoneNumber)) {
          setNumberErr('Please enter a valid 10-digit phone number.');
          setloader(false);
          return;
        }

        if (!password || passwordHealth !== 'strong') {
          seterr('Please enter a strong password.');
          setloader(false);
          return;
        }
      }

      setloader(true);

      // Build payload based on userType
      const payload = {
        token: myToken,
        email,
        no_plan: userType !== 'quick', // true for quick user, false for withPlan
      };

      // Add additional fields for 'withPlan' users
      if (userType === 'withPlan') {
        payload.password = password;
        payload.firstname = firstName;
        payload.lastname = lastName;
        payload.mobile = phoneNumber;
      }

      // eslint-disable-next-line no-console
      console.log('Creating user with payload:', payload);

      axios
        .post(`${baseUrl}/userRegistration`, payload)
        .then(async (response) => {
          // Handle the API response
          // eslint-disable-next-line no-console
          console.log('User registration successful:', response.data);
          setapiCALL(response.data);

          // Check subscription key and redirect accordingly
          if (response.data?.subscription === true) {
            // Manager has subscription - switch to created user's account
            const userDetails = response.data?.data?.userDetails?.[0];
            if (userDetails) {
              await switchToUserAccount(userDetails);
              // navigate('/plans');
            } else {
              navigate('/affiliate');
            }
          } else {
            // Manager doesn't have subscription - go to plans page
            navigate('/affiliate');
          }

          setloader(false);
        })
        .catch((error) => {
          // Handle errors
          // eslint-disable-next-line no-console
          console.error('User registration error:', error);
          setloader(false);
        });
    } catch (error) {
      // Handle errors from the updateProfile function
      // eslint-disable-next-line no-console
      console.error(error);
      setloader(false);
    }
  };
  return (
    <div className="widthController">
      <button
        type="button"
        className="backButton"
        onClick={() => navigate('/settings/manage-users')}
      >
        BACK
      </button>
      <div>
        <div className="flexSyntdom">
          <img className="Logosz" src={Logo} alt="" />
        </div>
        <div className="middleFledge">
          <p className="newAccount">Create account for new user</p>
          <p className="Enter">Enter the information of the new user</p>
          <div style={{ position: 'relative' }}>
            <input
              placeholder="Email"
              className="inputFull"
              // eslint-disable-next-line
              isLoading={emailExists}
              type="email"
              value={email}
              onChange={handleEmailChange}
              autoFocus
            />
            {email.includes('@')
              && email.includes('.')
              && email.split('.').pop().length >= 2
              && isLoading && (
                <div className="positionOfLoader">
                  <div
                    className={cn({
                      [styles.spinnerContainer]: true,
                      [styles.loading]: true,
                    })}
                  >
                    <div
                      style={{ borderColor: '#ffffff44' }}
                      className={styles.spinner}
                    />
                    <div
                      style={{ borderColor: '#5aa5ef' }}
                      className={cn({
                        [styles.spinner]: true,
                        [styles.animatedSpinner]: true,
                      })}
                    />
                  </div>
                </div>
              )}
          </div>
          {emailExists?.message === 'Email already exists' && (
            <label className="redisher">{emailExists?.message || err}</label>
          )}
          <label className="redisher">{err}</label>

          {/* Additional fields for 'withPlan' user type */}
          {userType === 'withPlan' && (
            <>
              <div className="flexMan">
                <input
                  placeholder="First Name"
                  className="inputFull"
                  type="text"
                  value={firstName}
                  onChange={handleFirstNameChange}
                />
                <input
                  placeholder="Last Name"
                  className="inputFull"
                  type="text"
                  value={lastName}
                  onChange={handleLastNameChange}
                />
              </div>
              <PhoneInput
                placeholder="Phone Number"
                value={phoneNumber}
                className="inputFull"
                onChange={handlePhoneNumberChange}
                countryCallingCodeEditable={false}
                defaultCountry="US"
              />
              {phoneNumber && !validatePhoneNumber(phoneNumber) && (
                <label className="redisher">Phone number must be 10 digits</label>
              )}
              {numberErr && <label className="redisher">{numberErr}</label>}
              <div className="relativez">
                <input
                  placeholder="Password"
                  className="inputFull"
                  label="Enter a password"
                  type={showPassword ? 'text' : 'password'}
                  minLength={12}
                  maxLength={30}
                  autoComplete="new-password"
                  value={password}
                  onChange={handlePasswordChange}
                />
                <span
                  role="button"
                  tabIndex={0}
                  className="eyeCatcher"
                  onKeyDown={() => { }}
                  onClick={handleTogglePasswordVisibility}
                >
                  {showPassword ? (
                    <HiddenIcon className={styles.passwordVisibilityToggleIcon} />
                  ) : (
                    <VisibleIcon className={styles.passwordVisibilityToggleIcon} />
                  )}
                </span>
              </div>
            </>
          )}

          <div className="Thebg">
            {userType === 'withPlan' && password ? (
              !password || passwordHealth === 'strong' ? (
                <ProjectorAcknowlegingTxt />
              ) : (
                <PasswordRequirements password={password} />
              )
            ) : (
              <ProjectorAcknowlegingTxt />
            )}
            <button
              onClick={
                emailRegex.test(email)
                  && (userType === 'quick'
                    || (userType === 'withPlan' && firstName && lastName && phoneNumber && password && passwordHealth === 'strong'))
                  && !loader
                  ? handleSubmit
                  : null
              }
              disabled={loader}
              className={
                !emailRegex.test(email)
                  || emailExists?.message === 'Email already exists'
                  || (userType === 'withPlan' && (!firstName || !lastName || !phoneNumber || !password || passwordHealth !== 'strong'))
                  || loader
                  ? 'disabledaggre'
                  : 'aggre'
              }
            >
              {loader === true ? (
                <svg className="loading-circle" viewBox="0 0 50 50">
                  <circle
                    className="circle"
                    cx="25"
                    cy="25"
                    r="20"
                    fill="none"
                    strokeWidth="4"
                  />
                </svg>
              ) : (
                'AGREE AND CONTINUE'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateUser;
